/**
 * Infobulle et légende.
 *
 * Trois règles portées par ce fichier :
 *   · une seule infobulle liste TOUTES les séries à ce X — le pointeur n'a
 *     jamais à atterrir sur une ligne précise pour obtenir un chiffre ;
 *   · la valeur mène, le nom de série suit — à ce moment-là le lecteur a déjà
 *     la série sous le pointeur et veut le nombre ;
 *   · les noms de série viennent de l'API : ils sont insérés en tant que texte
 *     React (jamais `dangerouslySetInnerHTML`), donc échappés.
 */

import type { ReactNode } from 'react';

interface TooltipEntry {
  name?: ReactNode;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
  /** Met en forme l'en-tête (souvent une date `YYYY-MM-DD`). */
  labelFormatter?: (label: string) => string;
  /** Met en forme chaque valeur. */
  valueFormatter?: (value: number) => string;
}

export function ChartTooltip({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter = (value) => String(value),
}: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const heading = labelFormatter && typeof label === 'string' ? labelFormatter(label) : String(label ?? '');

  return (
    <div className="tooltip" role="tooltip">
      <p className="tooltip__title">{heading}</p>
      {payload.map((entry, index) => (
        <div className="tooltip__row" key={entry.dataKey ?? index}>
          {/* Cle en trait court et non en pave : a cette densite, un carre
              plein met du poids de donnee au service d'une etiquette. */}
          <span
            className="legend__key"
            style={{ background: entry.color }}
            aria-hidden="true"
          />
          <span className="tooltip__name">{entry.name}</span>
          <span className="tooltip__value">
            {typeof entry.value === 'number' ? valueFormatter(entry.value) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export interface LegendEntry {
  label: string;
  color: string;
  /** `area` pour un aplat, `dashed` pour un pointillé, sinon un trait — la légende reprend la forme de la marque. */
  shape?: 'line' | 'area' | 'dashed';
}

/**
 * Légende maison.
 *
 * Présente dès deux séries, absente pour une seule : un cartouche à un seul
 * pastille ne fait que répéter le titre.
 */
export function ChartLegend({ entries }: { entries: readonly LegendEntry[] }) {
  if (entries.length < 2) return null;

  return (
    <ul className="legend">
      {entries.map((entry) => (
        <li className="legend__item" key={entry.label}>
          <span
            className={
              entry.shape === 'area' ? 'legend__key legend__key--area' : entry.shape === 'dashed' ? 'legend__key legend__key--dashed' : 'legend__key'
            }
            style={
              entry.shape === 'dashed'
                ? { background: `repeating-linear-gradient(90deg, ${entry.color} 0 4px, transparent 4px 7px)` }
                : { background: entry.color }
            }
            aria-hidden="true"
          />
          {entry.label}
        </li>
      ))}
    </ul>
  );
}
